import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { BellRing, ChevronDown, ChevronUp, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export function PushCoverageCard() {
  const [open, setOpen] = useState(false);
  const { data, refetch } = useQuery({ queryKey: ["push-coverage"], queryFn: async () => (await api.get("/push/coverage")).data });
  const testMe = async () => {
    try {
      const r = (await api.post("/push/test", { title: "RDM ClassRoom", body: "Notifica di prova dalla bacheca", url: "/scuola/avvisi" })).data;
      r.delivered ? toast.success(`Inviata a ${r.delivered} dispositivo/i: controlla il telefono`) : toast.error("Non hai dispositivi registrati o l'invio è fallito: attiva le notifiche su questo dispositivo");
      refetch();
    } catch { toast.error("Invio non riuscito"); }
  };
  if (!data) return null;
  const pct = data.total ? Math.round((data.reachable / data.total) * 100) : 0;
  const missing = data.students.filter((s) => !s.devices);
  return (
    <Card className="p-4 mb-5 border border-stone-200 shadow-none rounded-xl" data-testid="push-coverage">
      <div className="flex flex-wrap items-center gap-4">
        <BellRing size={18} className={pct >= 80 ? "text-emerald-600" : "text-amber-600"}/>
        <div className="flex-1 min-w-[220px]">
          <div className="text-sm font-medium text-stone-800">
            Notifiche push: <b data-testid="push-coverage-count">{data.reachable} su {data.total}</b> studenti attivi le ricevono sul telefono ({pct}%)
          </div>
          <div className="h-1.5 bg-stone-100 rounded-full mt-2 overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${pct}%` }}/></div>
          <div className="text-xs text-stone-500 mt-1.5">Gli altri vedono l'avviso solo aprendo la bacheca. Chiedi loro di aprire l'app e premere "Attiva notifiche" (su iPhone prima "Aggiungi a Home").</div>
        </div>
        {missing.length > 0 && (
          <button onClick={() => setOpen(!open)} className="text-xs text-[#1C352D] inline-flex items-center gap-1 underline" data-testid="push-coverage-toggle">
            {open ? <ChevronUp size={13}/> : <ChevronDown size={13}/>} {missing.length} senza notifiche
          </button>
        )}
      </div>
      {!data.vapid_ok && <div className="mt-3 text-xs text-red-700 flex items-center gap-1"><AlertTriangle size={13}/> Chiavi push del server mancanti o non valide (la chiave privata non corrisponde a quella pubblica): nessuna notifica può partire.</div>}
      <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-stone-600" data-testid="push-recent">
        <span>Ultimi invii: <b className="text-emerald-700">{data.recent.ok} consegnati</b> · <b className={data.recent.failed ? "text-red-700" : ""}>{data.recent.failed} non riusciti</b>{data.recent.last_at ? ` · ultimo ${new Date(data.recent.last_at).toLocaleString("it-IT")}` : " · nessun invio registrato"}</span>
        <button onClick={testMe} className="underline text-[#1C352D]" data-testid="push-test-me">Invia una notifica di prova a me</button>
      </div>
      {data.recent.errors.length > 0 && (
        <div className="mt-2 text-[11px] text-red-800 bg-red-50 border border-red-200 rounded-md px-3 py-2 space-y-0.5" data-testid="push-errors">
          {data.recent.errors.map((e) => <div key={e.key}><b>{e.key}</b> ×{e.count}: {e.sample}</div>)}
        </div>
      )}
      {open && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="push-coverage-missing">
          {missing.map((s) => (
            <span key={s.student_id} className="text-xs px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-900">
              {s.name}{!s.has_account && " · senza account portale"}
            </span>
          ))}
        </div>
      )}
    </Card>
  );
}
